import { z } from 'zod';
import { clientCategorySchema } from './client.js';
import { lotPipelineSchema, lotPipelineStageSchema } from './lot-pipeline.js';
import { lotStatusSchema } from './lot.js';
import { inquiryStatusSchema } from '../inquiry/status.js';
import { reportSummarySchema } from '../inspection/report.js';
import { personSchema } from '../people/person.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * L'adresse postale, **aplatie** — ce que l'en-tête de la fiche imprime : « 14 Bd des Martyrs »
 * puis « 44000 Nantes ».
 *
 * ⚠️ **La ville n'est pas une colonne d'`Address`** : elle vient de la commune (M8), dont le code
 * INSEE est la clé que l'adresse stocke. Le code postal, lui, est celui de l'adresse quand la
 * source l'a donné (`Address.postal_code`), celui de la commune sinon — cf. `inquiryAddressSchema`.
 *
 * `inseeCode` voyage avec eux parce que c'est lui qu'un formulaire d'édition repose dans son
 * sélecteur de commune — un couple « code postal + ville » ne désigne pas une ligne.
 */
export const clientSheetAddressSchema = z.object({
    inseeCode: z.string(),
    /** Le numéro et la rue ensemble, comme partout ailleurs. `null` tant que rien n'a été saisi. */
    street: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
    /** La commune déléguée, `null` sans elle — cf. `inquiryAddressSchema`. */
    delegatedName: z.string().nullable(),
});
/**
 * Une ligne de l'« Historique des lots » : la campagne, et **où elle en est**.
 *
 * ⚠️ `pipeline` est compté en base (D24), lot par lot, dans la requête qui remonte cette liste.
 * Une fiche à cinquante lots n'a donc pas à descendre cinquante listes de demandes pour en
 * additionner les étapes — c'était le seul moyen de dessiner cette barre avant, et il ne passe
 * pas l'échelle d'un bailleur.
 */
export const clientSheetLotSchema = z.object({
    id: uuidV7Schema,
    code: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    /** Le nombre de logements annoncé — les « 120 maisons » de la ligne. */
    unitCount: z.number().int().nullable(),
    startDate: z.iso.date().nullable(),
    status: lotStatusSchema,
    pipeline: lotPipelineSchema,
});
/** Au-delà, la fiche n'est plus une fiche : elle renvoie au tableau des demandes, filtré. */
export const MAX_CLIENT_SHEET_INQUIRIES = 50;
/**
 * Une demande **hors lot** du client — la carte « Contrôle unique » d'un particulier, et les
 * commandes ponctuelles (une vente, un contrôle à la demande) d'un client professionnel.
 *
 * `stage` est l'étape du funnel (`lotPipelineStageOf`), la pastille de la carte : même partition
 * que la barre, donc jamais en désaccord avec elle. `null` pour une demande abandonnée.
 */
export const clientSheetInquirySchema = z.object({
    id: uuidV7Schema,
    reference: z.string().nullable(),
    status: inquiryStatusSchema,
    stage: lotPipelineStageSchema.nullable(),
    /** Le type de contrôle, champ libre (M2, M13) — le « motif » de la carte. */
    purpose: z.string().nullable(),
    address: clientSheetAddressSchema,
    createdAt: z.iso.datetime({ offset: true }),
    /** Le rapport **du dernier contrôle**, une fois généré — ce qui arme le bouton « Rapport ». */
    report: reportSummarySchema.nullable(),
});
/**
 * La **fiche client** telle que l'API la sert : l'en-tête, le bandeau de contact, l'avancement
 * cumulé, l'historique des lots et les demandes hors lot — en une seule lecture.
 *
 * ⚠️ **Le donneur d'ordre d'une demande se lit par ses deux chemins, M32 devant M14** :
 * `Inquiry.client_id`, sinon le client du lot de son lieu — la règle du tableau de bord et de la
 * file d'export. Une demande commandée par un autre client sur un lieu de la campagne n'est donc
 * pas comptée dans le lot : elle ne fait pas partie de la campagne.
 *
 * ⚠️ **`pipeline` est compté en base (D24), pas additionné au navigateur** — et il vaut, à la
 * demande près, la somme des lots **plus** les demandes hors lot : c'est ce qui permet au
 * « Contrôle unique » d'un particulier, qui n'a aucun lot, d'avoir une barre.
 *
 * ⚠️ Les lots ne sont **pas paginés** : c'est une section de fiche, pas une liste d'écran, et la
 * maquette les déroule tous. Ce qui coûtait cher — l'avancement — est compté par le serveur.
 */
export const clientSheetSchema = z.object({
    id: uuidV7Schema,
    name: z.string(),
    category: clientCategorySchema,
    /** Sans contrôle de format, et absent chez un particulier — cf. `clientSchema`. */
    siret: z.string().nullable(),
    /** L'année d'entrée en relation — le « client depuis 2019 » de l'en-tête. */
    clientSince: z.number().int().nullable(),
    /** « Archivé le … » sur l'en-tête, et le geste qui le rend au répertoire — `null` sinon. */
    archivedAt: z.iso.datetime().nullable(),
    /**
     * Le contact référent (M15), tel quel : c'est `Person`, pas une forme parallèle.
     *
     * ⚠️ **La « Fonction » de la maquette n'y est pas** : `Person` n'a pas de colonne de rôle, et
     * ce lot n'ouvre pas de migration. Le champ existe sur le formulaire papier, pas encore dans le
     * modèle.
     */
    contact: personSchema.nullable(),
    address: clientSheetAddressSchema.nullable(),
    pipeline: lotPipelineSchema,
    lots: z.array(clientSheetLotSchema),
    /**
     * Les demandes **hors lot**, de la plus récente à la plus ancienne, au plus
     * `MAX_CLIENT_SHEET_INQUIRIES` — le « Contrôle unique » d'un particulier en est la première.
     */
    inquiries: z.array(clientSheetInquirySchema),
    /** Leur nombre total : `inquiries` peut en montrer moins, et l'écran le dit alors. */
    inquiryTotal: z.number().int().min(0),
});
//# sourceMappingURL=client-sheet.js.map